/**
 * Bob Laadt Op — Vragennav: samentrekken, markeren, pil verschuiven
 * (ontwerp: hoe-werkt-het.html, JS 2408–2470).
 *
 * De balk is sticky (de sectie-wrapper .bob-jumpnav-section, zie bob-jumpnav.css).
 * Hier:
 *  1. of hij vastgeklikt zit — dan trekt hij samen tot de pil-nav (.is-compact);
 *  2. welke sectie actief is (leeslijn 200px) en waar de oranje pil staat;
 *  3. op smalle schermen de actieve link binnen de balk naar het midden scrollen;
 *  4. het achtergrondvlak alleen over zijn eigen plek in de pagina (--jumpnav-slot);
 *  5. het einde van .jumpnav-scope uit het ontwerp nabootsen: zodra de onderkant van
 *     de sectie waar de LAATSTE link naar wijst boven de onderkant van de balk komt,
 *     schuift de balk mee omhoog (zoals sticky binnen een container).
 *
 * Scrollpositie via window.bobScroll: Horizon laat vanaf 990px .page-wrapper scrollen.
 * Custom element: na een re-render in de theme editor start hij vanzelf opnieuw.
 */
const LEESLIJN = 200;

class BobJumpnav extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || this.parentElement;
    this.bar = this.querySelector('.jumpnav-inner');
    this.pill = this.querySelector('.jumpnav-pill');
    this.links = Array.from(this.querySelectorAll('.jumpnav-inner a'));
    if (!this.section || !this.bar || !this.links.length) return;

    this.stilstaand = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.actief = -1;
    this.tick = false;

    this.onScroll = () => {
      if (this.tick) return;
      this.tick = true;
      requestAnimationFrame(() => {
        this.sync();
        this.tick = false;
      });
    };
    this.onResize = () => this.sync();

    /* Het samentrekken duurt .55s; tijdens die beweging veranderen de breedtes
       van de links, dus moet de pil meelopen. Eén keer meeliften op de
       transitie is genoeg — daarna doet scroll het werk weer. */
    this.onTransitionStart = (event) => {
      if (event.target !== this.bar) return;
      const t0 = performance.now();
      const volg = (nu) => {
        this.zetPil(this.links[this.actief]);
        if (nu - t0 < 650) requestAnimationFrame(volg);
      };
      requestAnimationFrame(volg);
    };

    window.bobScroll?.on(this.onScroll);
    window.addEventListener('resize', this.onResize);
    this.addEventListener('transitionstart', this.onTransitionStart);
    /* Andere secties opnieuw geladen in de editor → posities kunnen verschoven zijn */
    document.addEventListener('shopify:section:load', this.onResize);
    document.fonts?.ready.then(this.onResize);

    this.sync();
  }

  disconnectedCallback() {
    if (!this.onScroll) return;
    window.bobScroll?.off(this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.removeEventListener('transitionstart', this.onTransitionStart);
    document.removeEventListener('shopify:section:load', this.onResize);
  }

  /** @param {HTMLElement | undefined} link */
  zetPil(link) {
    if (!this.pill) return;
    if (!link) {
      this.pill.style.opacity = '0';
      return;
    }
    this.pill.style.opacity = '1';
    this.pill.style.width = link.offsetWidth + 'px';
    this.pill.style.transform = 'translateX(' + link.offsetLeft + 'px)';
  }

  /** @param {HTMLElement} link */
  doel(link) {
    const anchor = link.dataset.anchor;
    return anchor ? document.getElementById(anchor) : null;
  }

  sync() {
    /* De wrapper is sticky; de transform voor het wegrollen staat op dit element,
       dus de wrapper-maten zijn de onverschoven posities. */
    const rect = this.section.getBoundingClientRect();
    const stickyTop = parseFloat(getComputedStyle(this.section).top) || 0;

    /* Vastgeklikt = de balk staat op zijn sticky-top. Een paar pixels speling,
       anders flikkert de klasse op subpixel-scroll. */
    this.classList.toggle('is-compact', rect.top <= stickyTop + 2);

    /* Achtergrond alleen over de eigen plek: de volgende sectie begint waar die
       plek ophoudt; wat daaronder doorschuift blijft zichtbaar. */
    const next = this.section.nextElementSibling;
    const slot = next
      ? Math.max(0, Math.min(rect.height, next.getBoundingClientRect().top - rect.top))
      : rect.height;
    this.style.setProperty('--jumpnav-slot', slot + 'px');

    /* Einde van de scope: meeschuiven met de onderkant van de laatste doelsectie.
       Begrensd, zodat hij niet eindeloos ver boven het beeld blijft hangen. */
    let shift = 0;
    const laatste = this.doel(this.links[this.links.length - 1]);
    if (laatste && this.section.compareDocumentPosition(laatste) & Node.DOCUMENT_POSITION_FOLLOWING) {
      shift = Math.min(0, laatste.getBoundingClientRect().bottom - rect.bottom);
      shift = Math.max(shift, -(rect.bottom + 80));
    }
    this.style.transform = shift ? 'translateY(' + shift + 'px)' : '';

    /* Actief is de laatste sectie waarvan de bovenkant de leeslijn
       (header + balk) al gepasseerd is. */
    let actief = -1;
    this.links.forEach((link, i) => {
      const sec = this.doel(link);
      if (sec && sec.getBoundingClientRect().top <= LEESLIJN) actief = i;
    });
    this.actief = actief;
    this.links.forEach((link, i) => link.classList.toggle('is-active', i === actief));
    const link = this.links[actief];
    this.zetPil(link);

    /* Op smalle schermen scrollt de balk zelf horizontaal. De actieve knop naar
       het midden schuiven — alleen bínnen de balk, niet de pagina. */
    if (link && this.bar.scrollWidth > this.bar.clientWidth) {
      const doel = link.offsetLeft - (this.bar.clientWidth - link.offsetWidth) / 2;
      this.bar.scrollTo({ left: doel, behavior: this.stilstaand ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('bob-jumpnav')) customElements.define('bob-jumpnav', BobJumpnav);
